import acerto from '@acerto/web-sdk';
import { gerarEventId, publicarEventoMarketing } from '@shared/marketing-tracking/utils/dataLayerMarketing';
import { isEventoMarketing } from '@shared/marketing-tracking/utils/eventosMarketing';
import { detectarDisplayMode } from '@shared/pwa/utils/displayMode';
import dayjs from 'dayjs';
import { type CopyUrgenciaTratamento, FEATURE_FLAGS } from '@/constants/featureFlags';
import type {
  IntencaoDeclarada,
  MotivoBloqueioCredito,
  OrigemAbaCredito,
  VinculoDeclarado,
} from '@/features/credito/types/qualificacao';
import { DeeplinkType, StatusDivida } from '@/models/enums';
import MeioPagamento, { meioPagamentoToString } from '@/models/enums/MeioPagamento';
import { IDivida } from '@/models/IDivida';
import { IPromessa } from '@/models/IPromessa';
import { BoletoResource, DividaPropostaResource, DividaResource, OfertaResource } from '@/models/resources';
import type { OrigemMargem } from '@/models/resources/credito/ConsentResource';
import { getDividaIdBaseUnica, getIdBaseUnica, getOfertaIdBaseUnica } from '@/modules/events/eventIds';
import type { VarianteCheckoutUnicaDivida } from '@/modules/events/payloads/types/checkoutUnicaDivida';
import { OrdemPosOrdenacaoDividaItem } from '@/modules/events/payloads/types/ordemPosOrdenacaoModelagem';
import type { TipoNegociacaoSeuFocoAgora, TipoSeuFocoAgora } from '@/modules/events/payloads/types/seuFocoAgora';
import adoptlyService from '@/services/AdoptlyService';
import queryStringService from '@/services/QueryStringService';
import type { TipoOrdenacao } from '@/services/ordering/types/TipoOrdenacao';
import serverTimeService from '@/services/ServerTimeService';
import sessionService from '@/services/SessionService';

type DividaPick<Keys extends keyof DividaResource> = Pick<DividaResource, Keys>;
type OfertaPick<Keys extends keyof OfertaResource> = Pick<OfertaResource, Keys>;

function formatarData(data?: Date) {
  return data ? dayjs(data).format('YYYY-MM-DD') : undefined;
}

export function atualizarUserId(hash: string) {
  acerto.setUserId(hash);
}

function getJti(): string | undefined {
  const jti = sessionService.getTokenMetadata()?.jti;
  return jti;
}

function getHash(): string | undefined {
  const hash = sessionService.getTokenMetadata()?.hash;
  return hash;
}

/**
 * Eventos de marketing levam o mesmo `event_id` ao eventhub e ao dataLayer, para cruzar o que o
 * CRM registrou com o que as plataformas de mídia receberam.
 */
function enviarEventoComIdentificadorSessao({
  eventId,
  ...evento
}: {
  categoria: string;
  nome: string;
  tipo: 'pageview' | 'click' | 'onsuccess' | 'onerror';
  payload?: object | undefined;
  eventId?: string;
}) {
  const hash = getHash();
  if (!acerto.getUserId()) {
    acerto.setUserId(hash);
  }

  const identificadorSessao = getJti();
  const eventIdMarketing = isEventoMarketing(evento.nome) ? (eventId ?? gerarEventId()) : undefined;

  acerto.sendEvent({
    ...evento,
    payload: {
      ...evento.payload,
      display_mode: detectarDisplayMode(),
      ...(identificadorSessao && { sessao: identificadorSessao }),
      ...(eventIdMarketing && { event_id: eventIdMarketing }),
    },
  });

  if (eventIdMarketing) {
    publicarEventoMarketing(evento.nome, eventIdMarketing);
  }
}

export function acertoAcessouPortal() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_portal',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoVisualizouLoadingCredores() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_loading_credores',
    tipo: 'pageview',
  });
}

export function acertoAcessouPaginaInicial(listaCredores: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_pagina_inicial',
    tipo: 'pageview',
    payload: {
      credor_visualizado: listaCredores,
    },
  });
}

export function acertoAcessouLogin() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_login',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoConfirmouCpf() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'confirmou_cpf',
    tipo: 'onsuccess',
  });
}

export function acertoAcessouConfirmarData() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_confirmar_data',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickEmLogin() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_em_login',
    tipo: 'onsuccess',
  });
}

export function acertoClickEntrarCnpj() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_entrar_cnpj',
    tipo: 'onsuccess',
  });
}

export function acertoClickEmLoginGoogle() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_em_login_google',
    tipo: 'onsuccess',
  });
}

export function acertoAcessouCadastro() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_cadastro',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickEmCadastrar() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_em_cadastrar',
    tipo: 'onsuccess',
  });
}

export function acertoAcessouCadastroNome() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_cadastro_nome',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoEnviouNome() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'enviou_nome',
    tipo: 'onsuccess',
  });
}

export function acertoCorrigirEmail() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'corrigir_email',
    tipo: 'click',
  });
}

export function acertoCorrigiuEmail() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'corrigiu_email',
    tipo: 'onsuccess',
  });
}

export function acertoClickEmCadastrarGoogle() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_em_cadastrar_google',
    tipo: 'click',
  });
}

export function acertoCadastroGoogleFinalizado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'cadastro_google_finalizado',
    tipo: 'onsuccess',
  });
}

export function acertoVisualizouEmailJaCadastrado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'visualizou_email_ja_cadastrado',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickAcessarConta() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_acessar_conta',
    tipo: 'click',
  });
}

export function acertoClickCadastrarComOutroEmail() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_cadastrar_com_outro_email',
    tipo: 'click',
  });
}

export function acertoAutenticacaoCompleta() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'autenticacao_completa',
    tipo: 'onsuccess',
  });
}

export function acertoAcessouLoginDeeplinkEmail() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_login_deeplink_email',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickEntrarDeeplinkEmail(dividaId: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_entrar_deeplink_email',
    tipo: 'onsuccess',
    payload: {
      divida_id: dividaId,
    },
  });
}

export function acertoAcessouCadastroDeeplinkEmail(tipoRotacionamentoContato?: string | null) {
  const payload: Record<string, unknown> = {
    ...queryStringService.getUtms(),
  };

  if (tipoRotacionamentoContato != null && tipoRotacionamentoContato !== '') {
    payload.tipo_rotacionamento_contato = tipoRotacionamentoContato;
  }

  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_cadastro_deeplink_email',
    tipo: 'pageview',
    payload,
  });
}

export function acertoClickConfirmarDeeplinkEmail(dividaId: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_confirmar_deeplink_email',
    tipo: 'onsuccess',
    payload: {
      divida_id: dividaId,
    },
  });
}

export function acertoAcessouLinkExpirado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_link_expirado',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoLogoutSessao(origem: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'logout_sessao',
    tipo: 'pageview',
    payload: {
      origem,
    },
  });
}

export function acertoAcessouFluxoDeeplink(params: {
  tipoDeeplink: DeeplinkType;
  dividaIdPrioritaria?: number;
  ofertaId?: number | string;
  rotacionamentoContato?: boolean;
  tipoRotacionamentoContato?: string | null;
}) {
  const payload: Record<string, unknown> = {
    ...queryStringService.getUtms(),
    tipo_deeplink: params.tipoDeeplink,
  };

  if (params.dividaIdPrioritaria) {
    payload.divida_id_prioritaria = params.dividaIdPrioritaria;
  }

  if (params.ofertaId !== undefined) {
    payload.oferta_id = params.ofertaId;
  }

  if (params.rotacionamentoContato !== undefined) {
    payload.rotacionamento_contato = params.rotacionamentoContato;
  }

  if (params.tipoRotacionamentoContato != null && params.tipoRotacionamentoContato !== '') {
    payload.tipo_rotacionamento_contato = params.tipoRotacionamentoContato;
  }

  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_fluxo_deeplink',
    tipo: 'pageview',
    payload,
  });
}

export function acertoClickAcessarPortalLinkExpirado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_acessar_portal_link_expirado',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAcessouLimiteExcedido() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_limite_excedido',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickAcessarPortalLimiteExcedido() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_acessar_portal_limite_excedido',
    tipo: 'click',
  });
}

export function acertoAcessouEmailJaValidado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'acessou_email_ja_validado',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickAcessarPortalEmailJaValidado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_acessar_portal_email_ja_validado',
    tipo: 'click',
  });
}

export interface OrdenacaoRecomendadaItem {
  credor: string;
  dividaId: number;
}

export function acertoOrdenouDividasRecomendacao(
  algoritmoKey?: string,
  ordenacaoRecomendada?: OrdenacaoRecomendadaItem[]
) {
  const payload: Record<string, unknown> = {
    ...queryStringService.getUtms(),
  };

  if (algoritmoKey) {
    payload.algoritmo_key = algoritmoKey;
  }

  if (ordenacaoRecomendada?.length) {
    payload.ordenacao_recomendada_acerto = ordenacaoRecomendada;
  }

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'ordenou_dividas_recomendacao_acerto',
    tipo: 'onsuccess',
    payload,
  });
}

export function acertoOrdemPosOrdenacaoModelagem(ordemDividas: OrdemPosOrdenacaoDividaItem[]) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'ordem_pos_ordenacao_modelagem',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ordem_dividas: ordemDividas,
    },
  });
}

export function acertoPriorizouExibicaoDividaPrime(params: {
  dividaId: number;
  codigoCredor: number;
  credor: string;
  posicaoModeloOrdenacao: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'priorizou_exibicao_divida_prime',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      divida_id: getDividaIdBaseUnica(params.codigoCredor, params.dividaId),
      credor: params.credor,
      posicao_modelo_ordenacao: params.posicaoModeloOrdenacao,
    },
  });
}

// #region - Eventos Divida
export function acertoDividaSincronizadaSucesso(
  api: string,
  dividas: { podeSerNegociada: boolean }[],
  latencias: { rede?: number; total: number }
) {
  const quantidadeDividasNegociaveis = dividas.filter(divida => divida.podeSerNegociada).length;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'divida_sincronizada_sucesso',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      credor_visualizado: api,
      quantidade_dividas_negociaveis: quantidadeDividasNegociaveis,
      latencia_rede: latencias.rede,
      latencia_total: latencias.total,
    },
  });
}

export function acertoDividaSincronizadaErro(
  credores: string,
  statusCode?: number,
  statusText?: string,
  exception?: string
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'divida_sincronizada_erro',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      credor_visualizado: credores,
      referrer: window.location.href,
      status_code: statusCode,
      status_text: statusText,
      exception,
    },
  });
}

export function acertoDividaSincronizouSucessoUnicoEndpoint() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'sincronizacao_dividas_stream_sucesso',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoDividaSincronizouErroUnicoEndpoint() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'sincronizacao_dividas_stream_erro',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoDividaSincronizouComErroUnicoEndpointCredor(credor: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'sincronizacao_dividas_credor_stream_erro',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      credor,
    },
  });
}

export function acertoAcessouAlgoDeuErrado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_algo_deu_errado',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      referrer: window.location.href,
    },
  });
}

export function acertoClickRecarregarPagina() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_recarregar_pagina',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      referrer: window.location.href,
    },
  });
}

export function acertoClickDivida(divida: DividaResource) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    status: divida.status,
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_divida',
    tipo: 'click',
    payload,
  });
}

export function acertoClickOpcoesNegociar(divida: DividaResource) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    status: divida.status,
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_opcoes_negociar',
    tipo: 'click',
    payload,
  });
}

export function acertoVisualizouDivida(
  divida: DividaResource,
  isDividaPrioritaria = false,
  tipoCard: 'padrao' | 'precreli' | 'ofertas_parciais_ou_integrais' = 'padrao'
) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    status: divida.status,
    fluxo_divida: divida.fluxo,
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
    tipo_card: tipoCard,
    ...(isDividaPrioritaria && { payload_divida: true }),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_divida',
    tipo: 'pageview',
    payload,
  });
}

export function acertoVisualizouDividaAbaMinhasDividas(divida: DividaResource) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    status: divida.status,
    fluxo_divida: divida.fluxo,
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_divida_aba_minhas_dividas',
    tipo: 'pageview',
    payload,
  });
}

export function acertoVisualizouDividaCardTest(divida: DividaResource) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    status: divida.status,
    fluxo_divida: divida.fluxo,
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_divida_card_teste',
    tipo: 'pageview',
    payload,
  });
}

/** Evita reenvio da exposição do novo card ao remontar a lista na mesma sessão da SPA. */
const novosCardsDividaVisualizados = new Set<string>();

/**
 * Exposição do card da experiência de checkout.
 * Grão: sessão × dívida.
 */
export function acertoVisualizouNovoCardDivida(divida: DividaPick<'id' | 'codigoCredor'>) {
  const dividaId = getIdBaseUnica({
    codigoCredor: divida.codigoCredor,
    idEntidade: divida.id,
  });
  const chaveExposicao = `${getJti() ?? ''}:${dividaId}`;

  if (novosCardsDividaVisualizados.has(chaveExposicao)) {
    return;
  }

  novosCardsDividaVisualizados.add(chaveExposicao);

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_novo_card_divida',
    tipo: 'pageview',
    payload: {
      divida_id: dividaId,
      tipo_card: 'padrao',
    },
  });
}

/** O bloco apareceu na tela. Enviado uma vez por montagem da home. */
export function acertoExibiuSeuFocoAgora(tipo: TipoSeuFocoAgora, proposta: IPromessa, parcela: BoletoResource) {
  const { codigoCredor } = proposta.divida;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'exibiu_seu_foco_agora',
    tipo: 'pageview',
    payload: {
      tipo,
      divida_id: getIdBaseUnica({ codigoCredor, idEntidade: proposta.divida.id }),
      proposta_id: getIdBaseUnica({ codigoCredor, idEntidade: proposta.id }),
      parcela_id: getIdBaseUnica({ codigoCredor, idEntidade: parcela.id }),
      credor: proposta.divida.credor,
      primeira_parcela: parcela.numeroParcela === 1,
    },
  });
}

/** O bloco de dívida em destaque (fase 2) apareceu na tela. Enviado uma vez por montagem da home. */
export function acertoExibiuSeuFocoAgoraDivida(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor' | 'ofuscada'>,
  ofertaId: OfertaResource['id'] | undefined
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'exibiu_seu_foco_agora',
    tipo: 'pageview',
    payload: {
      tipo: 'divida' satisfies TipoSeuFocoAgora,
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
      oferta_id: ofertaId === undefined ? null : getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
      divida_ofuscada: divida.ofuscada,
    },
  });
}

/**
 * Elegibilidade à dívida em destaque (fase 2), nos dois grupos do experimento: é a população da
 * análise, e quem de fato viu o bloco sai à parte em `exibiu_seu_foco_agora`. A regra de ordenação
 * chega pronta porque calculá-la aqui importaria as estratégias de ordenação, que importam este módulo.
 */
export function acertoElegivelSeuFocoAgoraDivida({
  divida,
  variante,
  tipoOrdenacao,
  algoritmoKey,
}: {
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>;
  variante: boolean;
  tipoOrdenacao: TipoOrdenacao;
  /** Mesma chave de `ordenou_dividas_recomendacao_acerto` (`modelagem_acerto`, `maior_receita`…). */
  algoritmoKey: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'elegivel_seu_foco_agora_divida',
    tipo: 'pageview',
    payload: {
      variante,
      tipo_ordenacao: tipoOrdenacao,
      algoritmo_key: algoritmoKey,
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
    },
  });
}

/** Toque no botão ou no link do bloco de dívida em destaque. Não substitui nenhum evento do card da lista. */
export function acertoClickSeuFocoAgoraDivida(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>,
  ofertaId: OfertaResource['id'],
  tipoNegociacao: TipoNegociacaoSeuFocoAgora
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_seu_foco_agora_divida',
    tipo: 'click',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
      credor: divida.credor,
      tipo_negociacao: tipoNegociacao,
    },
  });
}

/** Toque no botão de autenticação do bloco de dívida ofuscada: não há oferta a abrir, só o OTP. */
export function acertoClickAutenticarSeuFocoAgoraDivida(divida: DividaPick<'id' | 'codigoCredor' | 'credor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_autenticar_seu_foco_agora_divida',
    tipo: 'click',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
    },
  });
}

const dividasExpostasDestaqueCredorOriginarioEnviadas = new Set<string>();

/**
 * Login elegível ao teste `checkout_unica_divida_disponivel` (uma dívida disponível, sem acordo
 * pendente, sem oferta parcial), disparado nos dois braços no momento em que a regra é atendida.
 * É a base de comparação do experimento: o controle não tem outro evento próprio.
 * `deeplink` separa os logins que chegaram por link de dívida, também elegíveis.
 */
export function acertoElegivelCheckoutUnicaDivida(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>,
  variante: VarianteCheckoutUnicaDivida,
  deeplink: boolean
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'elegivel_checkout_unica_divida',
    tipo: 'onsuccess',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
      variante,
      deeplink,
    },
  });
}

/** Braço teste: o portal levou o login elegível da lista direto ao checkout da dívida. */
export function acertoRedirecionouCheckoutUnicaDivida(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>,
  ofertaId: OfertaResource['id']
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'redirecionou_checkout_unica_divida',
    tipo: 'onsuccess',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
      credor: divida.credor,
    },
  });
}

/** Braço teste: a saudação "Encontramos uma dívida para você negociar" apareceu acima do checkout. */
export function acertoExibiuSaudacaoCheckoutUnicaDivida(divida: DividaPick<'id' | 'codigoCredor' | 'credor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'exibiu_saudacao_checkout_unica_divida',
    tipo: 'pageview',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
    },
  });
}

/** Braço teste: o consumidor saiu do checkout redirecionado pelo "Ver todas as minhas dívidas". */
export function acertoClickVerTodasDividasCheckoutUnicaDivida(divida: DividaPick<'id' | 'codigoCredor' | 'credor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_ver_todas_dividas_checkout_unica_divida',
    tipo: 'click',
    payload: {
      divida_id: getDividaIdBaseUnica(divida.codigoCredor, divida.id),
      credor: divida.credor,
    },
  });
}

/**
 * Exposição a uma dívida com credor originário, no máximo uma vez por dívida enquanto a SPA estiver
 * montada. `securitizadora` e `credor_originario` permitem os cortes por carteira direto do evento;
 * `header_destaque` indica se o destaque foi de fato renderizado.
 *
 * `exibiu_nome_originario` separa quem viu o nome do originário de quem ficou só com as iniciais no
 * círculo por nome longo. Vai ausente quando o destaque não é renderizado.
 */
export function acertoVisualizouHeaderDestaqueCredorOriginario(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>,
  headerDestaque: boolean,
  credorOriginario?: string,
  exibiuNomeOriginario?: boolean
) {
  const dividaId = getIdBaseUnica({
    codigoCredor: divida.codigoCredor,
    idEntidade: divida.id,
  });

  if (dividasExpostasDestaqueCredorOriginarioEnviadas.has(dividaId)) {
    return;
  }

  dividasExpostasDestaqueCredorOriginarioEnviadas.add(dividaId);

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_header_destaque_credor_originario',
    tipo: 'pageview',
    payload: {
      divida_id: dividaId,
      securitizadora: divida.credor,
      header_destaque: headerDestaque,
      ...(credorOriginario !== undefined && { credor_originario: credorOriginario }),
      ...(exibiuNomeOriginario !== undefined && { exibiu_nome_originario: exibiuNomeOriginario }),
    },
  });
}

export function acertoResumoProposta(proposta: IPromessa) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.divida.id,
    }),
    status_proposta: proposta.status,
    valor_vencido: proposta.divida.valorPrincipal,
    valor_total_atualizado: proposta.divida.valorTotal,
    data_origem: formatarData(proposta.divida.dataOrigem),
    data_chegada: formatarData(proposta.divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'resumo_proposta',
    tipo: 'click',
    payload,
  });
}

export function acertoResumoPropostaQuitada(divida: DividaPropostaResource) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    valor_vencido: divida.valorPrincipal,
    valor_total_atualizado: divida.valorTotal,
    data_origem: formatarData(divida.dataOrigem),
    data_chegada: formatarData(divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'resumo_proposta_quitada',
    tipo: 'click',
    payload,
  });
}

export function acertoClickRecomecarNegociacao(proposta: IPromessa) {
  if (!proposta) return;

  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.divida.id,
    }),
    proposta_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.id,
    }),
    status: StatusDivida.AguardandoNegociacao,
    valor_vencido: proposta.divida.valorPrincipal,
    valor_total_atualizado: proposta.divida.valorTotal,
    data_origem: formatarData(proposta.divida.dataOrigem),
    data_chegada: formatarData(proposta.divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_recomecar_negociacao',
    tipo: 'click',
    payload,
  });
}

export function acertoClickAcessarPortalDividaDesativada() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_acessar_portal_divida_desativada',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickDetalhesHome(proposta: IPromessa) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.divida.id,
    }),
    status_proposta: proposta.status,
    valor_vencido: proposta.divida.valorPrincipal,
    valor_total_atualizado: proposta.divida.valorTotal,
    data_origem: formatarData(proposta.divida.dataOrigem),
    data_chegada: formatarData(proposta.divida.dataChegada),
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_detalhes_home',
    tipo: 'click',
    payload,
  });
}

export function acertoAcessouAutenticacao() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_autenticacao',
    tipo: 'pageview',
  });
}

export function acertoClickConfirmarAutenticacao() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_confirmar_autenticacao',
    tipo: 'click',
  });
}

export function acertoVisualizouListaDividas(listaCredores: string, ordemDividasCredores?: OrdenacaoRecomendadaItem[]) {
  const payload: Record<string, unknown> = {
    ...queryStringService.getUtms(),
    credores_com_divida_negociavel: listaCredores,
  };

  if (ordemDividasCredores?.length) {
    payload.ordem_de_divida_e_credores_visualizado = ordemDividasCredores;
  }

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_lista_dividas',
    tipo: 'pageview',
    payload,
  });
}

export function acertoVisualizouListaAcordo() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_lista_acordo',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoVisualizouVerTodasAsDividas() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_ver_todas_as_dividas',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickVerTodasAsDividasCtaDividasHome() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_ver_todas_as_dividas_cta_dividas_home',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoExpandiuOutrasDividas() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'expandiu_outras_dividas',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoVisualizouSemDivida() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_sem_divida',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoVisualizouSemAcordo() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_sem_acordo',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoClickAbaDividas() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_aba_dividas',
    tipo: 'click',
  });
}

export function acertoClickAbaAcordos() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_aba_acordos',
    tipo: 'click',
  });
}

export function acertoClickEmSair() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_em_sair',
    tipo: 'click',
  });
}

export function acertoClickNegociarPrecreli(divida: DividaPick<'id' | 'codigoCredor' | 'valorTotal'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_negociar_precreli',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoAcessouDividaDesativadaDeeplink() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_divida_desativada_deeplink',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAcessouErroGenerico() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_erro_generico',
    tipo: 'onerror',
  });
}

export function acertoAcessouErroAtualizacaoDivida(divida: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_erro_atualizacao_divida',
    tipo: 'onerror',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickVoltarPaginaInicialErroGenerico() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_voltar_pagina_inicial_erro_generico',
    tipo: 'click',
  });
}

export function acertoClickAtualizarDivida(divida: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_atualizar_divida',
    tipo: 'onerror',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

// #endregion

// #region - Eventos Oferta

export function acertoClickNegociarParcial(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_negociar_parcial',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

export function acertoClickNegociarTotal(divida: DividaResource) {
  const melhorOfertaId = divida?.melhorOferta?.id ?? null;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_negociar_total',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      id_melhor_oferta: melhorOfertaId != null ? getOfertaIdBaseUnica(divida.codigoCredor, melhorOfertaId) : null,
    },
  });
}

export function acertoClickDetalhes(divida: IDivida) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_detalhes',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickVerOutrasOfertas(divida: DividaResource, melhorOfertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_ver_outras_ofertas',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      id_melhor_oferta: getOfertaIdBaseUnica(divida.codigoCredor, melhorOfertaId),
    },
  });
}

export function acertoClickDividaColapsada(divida: DividaResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_divida_colapsada',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickQueroEssaOferta(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_quero_essa_oferta',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

/** Clique no CTA principal do card da nova experiência. Não substitui `click_quero_essa_oferta`. */
export function acertoClickNovoCardDividaNegociar(divida: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_novo_card_divida_negociar',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickSelecionouOferta(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_selecionou_oferta',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

export function acertoClickConfirmarOferta(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_confirmar_oferta',
    tipo: 'onsuccess',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

export function acertoClickSimularOfertas(divida: DividaResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_simular_ofertas',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickEscolherParcelamento(divida: DividaResource, quantidadeParcelas: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_escolher_parcelamento',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      quantidade_parcelas: quantidadeParcelas,
    },
  });
}

export function acertoConfirmarOfertasDoSimulador(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_confirmar_ofertas_do_simulador',
    tipo: 'onsuccess',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

export function acertoClickSimularNovamente(divida: DividaResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_simular_novamente',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoAcessouTipoNegociacao(divida: DividaResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_tipo_negociacao',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

export function acertoClickAvancarNegociarParcial(divida: DividaResource, ofertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_avancar_negociar_parcial',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
    },
  });
}

export function acertoClickAvancarNegociarTotal(divida: DividaResource, melhorOfertaId: OfertaResource['id']) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_avancar_negociar_total',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      id_melhor_oferta: getOfertaIdBaseUnica(divida.codigoCredor, melhorOfertaId),
    },
  });
}

export function acertoClickAlterarDataVencimento(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_alterar_data_vencimento',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
      quantidade_parcelas: oferta.quantidadeParcelas,
    },
  });
}

export function acertoAcessouErroAlterarDataVencimento(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_erro_alterar_data_vencimento',
    tipo: 'onerror',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
      quantidade_parcelas: oferta.quantidadeParcelas,
    },
  });
}

export function acertoClickSelecionouDataVencimento(params: {
  divida: DividaPick<'id' | 'codigoCredor'>;
  oferta: OfertaPick<'id'>;
  data: Date;
}) {
  const { divida, oferta, data } = params;
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_selecionou_data_vencimento',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
      data_vencimento_selecionada: formatarData(data),
    },
  });
}

export function acertoClickConfirmarDataVencimento(params: {
  divida: DividaPick<'id' | 'codigoCredor'>;
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>;
  data: Date;
}) {
  const { divida, oferta, data } = params;
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_confirmar_data_vencimento',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
      quantidade_parcelas: oferta.quantidadeParcelas,
      data_vencimento_selecionada: formatarData(data),
    },
  });
}

export function acertoVisualizouOferta(
  divida: DividaPick<'id' | 'codigoCredor' | 'credor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas' | 'valorParcela' | 'valorEntrada'>
) {
  const payload = {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
    oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    credor: divida.credor,
    valor_entrada: oferta.valorEntrada,
    valor_parcela: oferta.valorParcela,
    quantidade_parcelas: oferta.quantidadeParcelas,
  };

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_oferta',
    tipo: 'pageview',
    payload,
  });

  adoptlyService.trackEvent('visualizou_oferta', payload);
}

export function acertoAcessouResumoOferta(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_resumo_oferta',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
      quantidade_parcelas: oferta.quantidadeParcelas,
    },
  });
}

export function acertoAcessouOpcaoPagamento(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_opcao_pagamento',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    },
  });
}

export function acertoClickContinuarDetalhesOferta(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_continuar_detalhes_oferta',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    },
  });
}

export function acertoClickSelecionarPix(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_selecionar_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    },
  });
}

export function acertoClickSelecionarBoleto(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_selecionar_boleto',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    },
  });
}

export function acertoClickConfirmarAcordo(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_confirmar_acordo',
    tipo: 'onsuccess',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
    },
  });
}

export function acertoAcessouVerOutrasOfertas(divida: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_ver_outras_ofertas',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

function payloadCheckoutDivida(divida: DividaPick<'id' | 'codigoCredor'>) {
  return {
    divida_id: getIdBaseUnica({
      codigoCredor: divida.codigoCredor,
      idEntidade: divida.id,
    }),
  };
}

function payloadCheckoutDividaOferta(divida: DividaPick<'id' | 'codigoCredor'>, oferta: OfertaPick<'id'>) {
  return {
    ...payloadCheckoutDivida(divida),
    oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, oferta.id),
  };
}

/** Entrada no checkout da nova experiência, antes de qualquer interação. */
export function acertoAcessouCheckout(divida: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_checkout',
    tipo: 'pageview',
    payload: payloadCheckoutDivida(divida),
  });
}

/** Clique no seletor de ofertas do checkout da nova experiência.
 * `quantidadeSelecoesDistintas` conta ids distintos na sessão — 1 = marcou uma vez, 2+ = comparou. */
export function acertoClickCheckoutSelecionarOferta(
  divida: DividaPick<'id' | 'codigoCredor'>,
  ofertaId: OfertaResource['id'],
  quantidadeSelecoesDistintas?: number
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_checkout_selecionar_oferta',
    tipo: 'click',
    payload: {
      ...payloadCheckoutDivida(divida),
      oferta_id: getOfertaIdBaseUnica(divida.codigoCredor, ofertaId),
      ...(quantidadeSelecoesDistintas !== undefined
        ? { quantidade_selecoes_distintas: quantidadeSelecoesDistintas }
        : {}),
    },
  });
}

/** Clique em “Alterar” no seletor de data do checkout. Seleção/confirmação da data já disparam no modal. */
export function acertoClickCheckoutSelecionarData(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_checkout_selecionar_data',
    tipo: 'click',
    payload: {
      ...payloadCheckoutDividaOferta(divida, oferta),
      quantidade_parcelas: oferta.quantidadeParcelas,
    },
  });
}

/** Clique no seletor de forma de pagamento da 1ª parcela no checkout. */
export function acertoClickCheckoutSelecionarFormaPagamento(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id'>,
  meio: MeioPagamento
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_checkout_selecionar_forma_pagamento',
    tipo: 'click',
    payload: {
      ...payloadCheckoutDividaOferta(divida, oferta),
      forma_pagamento: meioPagamentoToString(meio),
    },
  });
}

/** Clique em “Confirmar acordo” no checkout da nova experiência. */
export function acertoClickCheckoutConfirmarAcordo(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta: OfertaPick<'id' | 'quantidadeParcelas'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_checkout_confirmar_acordo',
    tipo: 'click',
    payload: {
      ...payloadCheckoutDividaOferta(divida, oferta),
      quantidade_parcelas: oferta.quantidadeParcelas,
    },
  });
}

/** Aceite do card de débito em conta no checkout. */
export function acertoClickCheckoutAceiteDebitoConta(
  divida: DividaPick<'id' | 'codigoCredor'>,
  oferta?: OfertaPick<'id'>
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_checkout_aceite_debito_conta',
    tipo: 'click',
    payload: oferta ? payloadCheckoutDividaOferta(divida, oferta) : payloadCheckoutDivida(divida),
  });
}

function payloadExitPopup(divida: DividaPick<'id' | 'codigoCredor'>, variante: CopyUrgenciaTratamento) {
  return {
    ...payloadCheckoutDivida(divida),
    variante,
  };
}

/** Impressão do exit popup de urgência no checkout. */
export function acertoVisualizouExitPopup(divida: DividaPick<'id' | 'codigoCredor'>, variante: CopyUrgenciaTratamento) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'visualizou_exitpopup',
    tipo: 'pageview',
    payload: payloadExitPopup(divida, variante),
  });
}

/** Clique em “Continuar negociação” no exit popup de urgência. */
export function acertoExitPopupContinuouNegociacao(
  divida: DividaPick<'id' | 'codigoCredor'>,
  variante: CopyUrgenciaTratamento
) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'exitpopup_continuou_negociacao',
    tipo: 'click',
    payload: payloadExitPopup(divida, variante),
  });
}

/** Clique em “Sair sem negociar” no exit popup de urgência. */
export function acertoExitPopupSaiu(divida: DividaPick<'id' | 'codigoCredor'>, variante: CopyUrgenciaTratamento) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'exitpopup_saiu',
    tipo: 'click',
    payload: payloadExitPopup(divida, variante),
  });
}

export function acertoClickVerTodasAsDividas() {
  enviarEventoComIdentificadorSessao({
    categoria: 'onboarding',
    nome: 'click_ver_todas_as_dividas',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      referrer: window.location.href,
    },
  });
}

/**
 * Proposta criada pelo checkout: é a "promessa" do indicador e a conversão principal das
 * campanhas. O `event_id` é derivado da proposta para que um reenvio pelo backend deduplique.
 */
export function acertoEnviouProposta(proposta: Pick<IPromessa, 'id' | 'status' | 'divida'>) {
  const propostaId = getIdBaseUnica({
    codigoCredor: proposta.divida.codigoCredor,
    idEntidade: proposta.id,
  });

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'enviou_proposta',
    tipo: 'onsuccess',
    eventId: `enviou_proposta_${propostaId}`,
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: propostaId,
      status_proposta: proposta.status,
    },
  });
}

// #endregion

// #region - Eventos Pagamento

export function acertoAcessouPaginaPagamento(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_pagina_pagamento',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      primeiraParcelaGerenciadaAcerto: proposta.parcelasGerenciadas.primeiraParcela,
      colchaoGerenciadoAcerto: proposta.parcelasGerenciadas.demaisParcelas,
    },
  });
}

export function acertoAcessouPaginaPropostaQuitada(proposta: IPromessa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_pagina_pagamento',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickBaixarBoleto(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_baixar_boleto',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickCopiarCodigoDeBarras(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_copiar_codigo_de_barras',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickSalvarCalendario(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_salvar_calendario',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickLiberarParcela(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_liberar_parcela',
    tipo: 'onsuccess',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickMaisParcelas(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_mais_parcelas',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickMenosParcelas(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_menos_parcelas',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickPagamentoDetalhesAcordo(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_pagamento_detalhe_acordo',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickCancelarAcordo(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'click_cancelar_acordo',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoAcessouCancelarAcordoDisponivel(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'acessou_cancelar_acordo_disponivel',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoAcessouCancelarAcordoIndisponivel(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'acessou_cancelar_acordo_indisponivel',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickQueroCancelar(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'click_quero_cancelar',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoAcessouAcordoCancelado(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'acessou_acordo_cancelado',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoAcessouCancelarAcordoForaHorario(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'cancelamento',
    nome: 'acessou_cancelar_acordo_fora_horario',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickPagarPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_pagar_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoAcessouPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'acessou_pagamento_pix',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickCopiarCodigoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_copiar_codigo_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

function criarEventoClickPagamento(nome: string) {
  return (proposta: IPromessa, boletoId: number) => {
    if (!proposta) return;

    enviarEventoComIdentificadorSessao({
      categoria: 'pagamento',
      nome,
      tipo: 'click',
      payload: {
        divida_id: getIdBaseUnica({
          codigoCredor: proposta.divida.codigoCredor,
          idEntidade: proposta.divida.id,
        }),
        proposta_id: getIdBaseUnica({
          codigoCredor: proposta.divida.codigoCredor,
          idEntidade: proposta.id,
        }),
        boleto_id: getIdBaseUnica({
          codigoCredor: proposta.divida.codigoCredor,
          idEntidade: boletoId,
        }),
      },
    });
  };
}

export const acertoClickQrCodePixProximoPagamento = criarEventoClickPagamento('click_qrCodePix_proximoPagamento');
export const acertoClickCopiaEColaPixProximoPagamento = criarEventoClickPagamento(
  'click_copiaEColaPix_proximoPagamento'
);
export const acertoClickBaixarBoletoProximoPagamento = criarEventoClickPagamento('click_baixarBoleto_proximoPagamento');
export const acertoClickCodigoBoletoProximoPagamento = criarEventoClickPagamento('click_codigoBoleto_proximoPagamento');

/** Indica que o usuário visualizou o lembrete de próximo pagamento pendente na Home. */
export function acertoVisualizouLembreteProximoPagamento(proposta: IPromessa, parcela: BoletoResource) {
  if (!proposta) return;

  // Mesma prioridade do card: Pix quando emitido; caso contrário, boleto.
  const metodoPagamentoPriorizado = parcela.pixEmitido ? 'pix' : 'boleto';

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'visualizou_lembrete_proximo_pagamento',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: parcela.id,
      }),
      credor: proposta.divida.credor,
      numero_parcela: parcela.numeroParcela,
      valor_parcela: parcela.valor,
      metodo_pagamento_priorizado: metodoPagamentoPriorizado,
    },
  });
}

function payloadToastVencimentoMeusAcordos(proposta: IPromessa, parcela: BoletoResource) {
  return {
    divida_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.divida.id,
    }),
    proposta_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: proposta.id,
    }),
    boleto_id: getIdBaseUnica({
      codigoCredor: proposta.divida.codigoCredor,
      idEntidade: parcela.id,
    }),
    credor: proposta.divida.credor,
    numero_parcela: parcela.numeroParcela,
    valor_parcela: parcela.valor,
  };
}

export function acertoToastVencimentoMeusAcordosDismiss(proposta: IPromessa, parcela: BoletoResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'toast_vencimento_meus_acordos_dismiss',
    tipo: 'click',
    payload: payloadToastVencimentoMeusAcordos(proposta, parcela),
  });
}

export function acertoToastVencimentoMeusAcordosPagarClick(proposta: IPromessa, parcela: BoletoResource) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_pagar_agora_toast_vencimento_meus_acordos',
    tipo: 'click',
    payload: payloadToastVencimentoMeusAcordos(proposta, parcela),
  });
}

/** Evita reenvio da exposição do card de acordo ao remontar a lista na mesma sessão da SPA. */
const cardsAcordoNovaExperienciaVisualizados = new Set<string>();

/**
 * Exposição do card da nova experiência de acordos.
 * Grão: sessão × proposta.
 */
export function acertoVisualizouCardAcordoNovaExperiencia(proposta: IPromessa) {
  if (!proposta) return;

  const propostaId = getIdBaseUnica({
    codigoCredor: proposta.divida.codigoCredor,
    idEntidade: proposta.id,
  });
  const chaveExposicao = `${getJti() ?? ''}:${propostaId}`;

  if (cardsAcordoNovaExperienciaVisualizados.has(chaveExposicao)) {
    return;
  }

  cardsAcordoNovaExperienciaVisualizados.add(chaveExposicao);

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'visualizou_card_acordo_nova_experiencia',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: propostaId,
      credor: proposta.divida.credor,
    },
  });
}

export const acertoClickQrCodePixCardAcordo = criarEventoClickPagamento('click_qrCodePix_cardAcordo');
export const acertoClickCopiaEColaPixCardAcordo = criarEventoClickPagamento('click_copiaEColaPix_cardAcordo');
export const acertoClickBaixarBoletoCardAcordo = criarEventoClickPagamento('click_baixarBoleto_cardAcordo');
export const acertoClickCodigoBoletoCardAcordo = criarEventoClickPagamento('click_codigoBoleto_cardAcordo');

export function acertoClickJaFizPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_ja_fiz_pagamento_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickDownloadDocumento(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_pix_download_documento',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoAcessouJaFizPagamento(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'acessou_ja_fiz_pagamento',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickContinuarJaFizPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_continuar_ja_fiz_pagamento_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickVoltarJaFizPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_voltar_ja_fiz_pagamento_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickSairJaFizPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_sair_ja_fiz_pagamento_pix',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoAcessouSucessoPagamentoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'acessou_sucesso_pagamento_pix',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickVerAcordo(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_ver_acordo',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoAcessouPagamentoNaoConfirmadoPix(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'acessou_pagamento_nao_confirmado_pix',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickVoltarPagamentoNaoConfirmado(proposta: IPromessa, boletoId: number) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_voltar_pagamento_nao_confirmado',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
      boleto_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: boletoId,
      }),
    },
  });
}

export function acertoClickAcessarAplicativo(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_acessar_aplicativo',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickAcessarPortalCredor(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_acessar_portal_credor',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickBaixarAppAndroid(proposta: IPromessa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_baixa_app_android',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickBaixarAppIos(proposta: IPromessa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pagamento',
    nome: 'click_baixa_app_ios',
    tipo: 'click',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

// #endregion

// #region - Eventos de Erros

export function acertoAcessouAlgoInesperado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_algo_inesperado_aconteceu',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoTentouNovamenteAlgoInesperado() {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_tentar_novamente_algo_inesperado_aconteceu',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAcessouNaoFoiPossivelCancelarAcordo(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_nao_foi_possivel_cancelar_acordo',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoClickVoltarNaoFoiPossivelCancelarAcordo(proposta: IPromessa) {
  if (!proposta) return;

  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'click_voltar_nao_foi_possivel_cancelar_acordo',
    tipo: 'pageview',
    payload: {
      divida_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.divida.id,
      }),
      proposta_id: getIdBaseUnica({
        codigoCredor: proposta.divida.codigoCredor,
        idEntidade: proposta.id,
      }),
    },
  });
}

export function acertoAcessouCardErro(url: string, componentePai: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_card_erro',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      url,
      componentePai,
    },
  });
}

export function acertoAcessouErroDivida(divida?: DividaResource) {
  if (!divida) return;
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'acessou_erro_divida',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      divida_id: getIdBaseUnica({
        codigoCredor: divida.codigoCredor,
        idEntidade: divida.id,
      }),
    },
  });
}

// #endregion

// #region - Eventos Credito Consignado CLT

/**
 * Remove chaves com valor `null`/`undefined`: o campo fica ausente do payload, e não presente com
 * valor vazio. Vários eventos da jornada só têm um campo quando a entidade (consentimento,
 * simulação) já existe, ou quando a falha é de um tipo específico — ver BE-23872.
 */
function semCamposVazios<T extends Record<string, unknown>>(campos: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(campos).filter(([, valor]) => valor !== null && valor !== undefined)
  ) as Partial<T>;
}

// -- Card "Empréstimo do Trabalhador" (lista de dívidas) --

export function acertoVisualizouOfertaEmprestimo(origemAba: OrigemAbaCredito) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'visualizou_oferta_credito_clt',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
      origem_aba: origemAba,
    },
  });
}

function acertoClicouOfertaEmprestimo(dataUa: string, origemAba: OrigemAbaCredito) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'clicou_oferta_emprestimo',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      data_ua: dataUa,
      origem_aba: origemAba,
    },
  });
}

/** Botão "Simular meu empréstimo", primeira dobra do bloco verde. */
export function acertoClicouOfertaTopo(origemAba: OrigemAbaCredito) {
  acertoClicouOfertaEmprestimo('click-portal-dividas-credito-clt-sim-topo', origemAba);
}

/** Botão do bloco do meio da página, depois do "como funciona" e do plano. */
export function acertoClicouOfertaMeio(origemAba: OrigemAbaCredito) {
  acertoClicouOfertaEmprestimo('click-portal-dividas-credito-clt-sim-meio', origemAba);
}

/** Botão "Sim, quero simular o empréstimo", sob a pergunta "Você trabalha com carteira assinada?". */
export function acertoClicouOfertaFinal(origemAba: OrigemAbaCredito) {
  acertoClicouOfertaEmprestimo('click-portal-dividas-credito-clt-sim-final', origemAba);
}

/** Barra fixa "Simular meu empréstimo", que some quando o CTA final entra na tela. */
export function acertoClicouOfertaBarraFlutuante(origemAba: OrigemAbaCredito) {
  acertoClicouOfertaEmprestimo('click-portal-dividas-credito-clt-sim-flutuante', origemAba);
}

// -- "Vamos ver quanto você pode pegar emprestado" (autorização) --

export function acertoAcessouAutorizacao() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'acessou_autorizacao',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAutorizouConsultaMargem() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'autorizou_consulta_margem',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAbriuPoliticaPrivacidade() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_politica_privacidade',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

// -- "Confirme seus dados" / "Seu endereço" (formulário de 3 passos) --

export function acertoAcessouFormularioDadosPessoais() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'acessou_formulario_dados_pessoais',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoConcluiuDadosPessoais(voltouPorErro: boolean) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'concluiu_dados_pessoais',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      voltou_por_erro: voltouPorErro,
    },
  });
}

export function acertoSelecionouEstadoCivil(estadoCivil: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'selecionou_estado_civil',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      estado_civil: estadoCivil,
    },
  });
}

export function acertoSelecionouNacionalidade(nacionalidade: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'selecionou_nacionalidade',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      nacionalidade,
    },
  });
}

export function acertoSelecionouProfissao(profissao: string) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'selecionou_profissao',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      profissao,
    },
  });
}

export function acertoConcluiuDadosContrato(voltouPorErro: boolean) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'concluiu_dados_contrato',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      voltou_por_erro: voltouPorErro,
    },
  });
}

export function acertoConcluiuEndereco(voltouPorErro: boolean) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'concluiu_endereco',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      voltou_por_erro: voltouPorErro,
    },
  });
}

export function acertoEncontrouCep(latenciaTotalMs: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'encontrou_cep',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      latencia_total: latenciaTotalMs,
    },
  });
}

export function acertoNaoEncontrouCep(motivoFalha: string, status: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'nao_encontrou_cep',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      motivo_falha: motivoFalha,
      status,
    },
  });
}

export function acertoEnviouDadosCadastrais(params: {
  consentimentoId: string | null;
  latenciaTotalMs: number;
  origemMargem?: OrigemMargem | null;
}) {
  const margemDoCache = params.origemMargem === 'cache';
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'enviou_dados_cadastrais',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({
        consentimento_id: params.consentimentoId,
        origem_margem: margemDoCache ? 'cache' : null,
      }),
      latencia_total: margemDoCache ? 0 : params.latenciaTotalMs,
    },
  });
}

export function acertoFalhouEnvioDados(params: {
  motivoFalha: string;
  status: number;
  camposInvalidos: string;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_envio_dados',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      motivo_falha: params.motivoFalha,
      status: params.status,
      campos_invalidos: params.camposInvalidos,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

// -- "Você pode pegar até" (simulação) --

export function acertoConsultouMargem(params: {
  consentimentoId: string | null;
  prazosDisponiveis: string;
  quantidadeParcelasRecomendada: number;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'consultou_margem',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      prazos_disponiveis: params.prazosDisponiveis,
      quantidade_parcelas_recomendada: params.quantidadeParcelasRecomendada,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

export function acertoFalhouConsultaMargem(params: {
  consentimentoId: string | null;
  status: number;
  exception: string | null;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_consulta_margem',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, exception: params.exception }),
      status: params.status,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

export function acertoVisualizouSimulacao(params: {
  consentimentoId: string | null;
  simulacaoId: string;
  valorSolicitado: number | null;
  valorLiberado: number;
  margemDisponivel: number;
  quantidadeParcelas: number;
  valorParcela: number;
  taxaMes: number;
  cetAno: number;
  comSeguro: boolean;
  alternativaSeguro: boolean;
  faixaValor: string | null;
  quantidadeTentativas: number;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'visualizou_simulacao',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({
        consentimento_id: params.consentimentoId,
        valor_solicitado: params.valorSolicitado,
        faixa_valor: params.faixaValor,
      }),
      simulacao_id: params.simulacaoId,
      valor_liberado: params.valorLiberado,
      margem_disponivel: params.margemDisponivel,
      quantidade_parcelas: params.quantidadeParcelas,
      valor_parcela: params.valorParcela,
      taxa_mes: params.taxaMes,
      cet_ano: params.cetAno,
      com_seguro: params.comSeguro,
      alternativa_seguro: params.alternativaSeguro,
      quantidade_tentativas: params.quantidadeTentativas,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

export function acertoNaoTeveOferta(params: {
  consentimentoId: string | null;
  motivoFalha: string;
  valorSolicitado: number | null;
  quantidadeParcelas: number;
  comSeguro: boolean;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'nao_teve_oferta',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, valor_solicitado: params.valorSolicitado }),
      motivo_falha: params.motivoFalha,
      quantidade_parcelas: params.quantidadeParcelas,
      com_seguro: params.comSeguro,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

/** `motivoFalha` é `'INDISPONIVEL'` (a API respondeu, mas não conseguiu ofertar) ou `'ERRO_CHAMADA'`
 * (a chamada não completou) — `exception` só existe no segundo caso. `comSeguro` mantém o
 * denominador completo também nas falhas técnicas (BE-23872, seção "Seguro"). */
export function acertoFalhouSimulacao(params: {
  consentimentoId: string | null;
  motivoFalha: 'INDISPONIVEL' | 'ERRO_CHAMADA';
  status: number;
  exception: string | null;
  comSeguro: boolean;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_simulacao',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, exception: params.exception }),
      motivo_falha: params.motivoFalha,
      status: params.status,
      com_seguro: params.comSeguro,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

/** `comSeguro` é a ponta que vale para o novo prazo, já depois do ajuste automático (ver
 * `seguroAjustado`) — nunca a ponta pedida antes de `ajustarSeguroAoPrazo` rodar. */
export function acertoAlterouPrazo(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  quantidadeParcelas: number;
  quantidadeParcelasAnterior: number;
  comSeguro: boolean;
  seguroAjustado: boolean;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'alterou_prazo',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      quantidade_parcelas: params.quantidadeParcelas,
      quantidade_parcelas_anterior: params.quantidadeParcelasAnterior,
      com_seguro: params.comSeguro,
      seguro_ajustado: params.seguroAjustado,
    },
  });
}

export function acertoSelecionouFaixaValor(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  faixaValor: string;
  valorSolicitado: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'selecionou_faixa_valor',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      faixa_valor: params.faixaValor,
      valor_solicitado: params.valorSolicitado,
    },
  });
}

export function acertoAbriuNumerosCompletos(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  valorLiberado: number;
  quantidadeParcelas: number;
  cetAno: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_numeros_completos',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      valor_liberado: params.valorLiberado,
      quantidade_parcelas: params.quantidadeParcelas,
      cet_ano: params.cetAno,
    },
  });
}

export function acertoAbriuValorManual(params: { consentimentoId: string | null; simulacaoId: string | null }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_valor_manual',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
    },
  });
}

export function acertoAlterouSeguro(params: {
  consentimentoId: string | null;
  comSeguro: boolean;
  valorLiberado: number;
  valorParcela: number;
  simulacaoId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'alterou_seguro',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      com_seguro: params.comSeguro,
      valor_liberado: params.valorLiberado,
      valor_parcela: params.valorParcela,
      simulacao_id: params.simulacaoId,
    },
  });
}

export function acertoAvancouDaOferta(params: { consentimentoId: string | null; simulacaoId: string }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'avancou_da_oferta',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      simulacao_id: params.simulacaoId,
    },
  });
}

export function acertoFechouSemOferta(params: { consentimentoId: string | null; motivoFalha: string }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'fechou_sem_oferta',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      motivo_falha: params.motivoFalha,
    },
  });
}

// -- "Onde você quer receber o dinheiro" --

export function acertoAlterouChavePix(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  tipoChave: string;
  exigeNovoConsentimento: boolean;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'alterou_chave_pix',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      tipo_chave: params.tipoChave,
      exige_novo_consentimento: params.exigeNovoConsentimento,
    },
  });
}

export function acertoConfirmouPedido(params: { consentimentoId: string | null; simulacaoId: string | null }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'confirmou_pedido',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
    },
  });
}

export function acertoEnviouPedido(params: {
  consentimentoId: string | null;
  propostaId: string;
  simulacaoId: string;
  valorLiberado: number;
  quantidadeParcelas: number;
  valorParcela: number;
  comSeguro: boolean;
  consentimentoRegravado: boolean;
  latenciaTotalMs: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'enviou_pedido',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      proposta_id: params.propostaId,
      simulacao_id: params.simulacaoId,
      valor_liberado: params.valorLiberado,
      quantidade_parcelas: params.quantidadeParcelas,
      valor_parcela: params.valorParcela,
      com_seguro: params.comSeguro,
      consentimento_regravado: params.consentimentoRegravado,
      latencia_total: params.latenciaTotalMs,
    },
  });
}

export function acertoFalhouEnvioPedido(params: {
  consentimentoId: string | null;
  etapaDaFalha: 'consentimento' | 'envio_pedido';
  motivoFalha: string;
  status: number;
  simulacaoId: string | null;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_envio_pedido',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      etapa_da_falha: params.etapaDaFalha,
      motivo_falha: params.motivoFalha,
      status: params.status,
    },
  });
}

// -- "Falta só a sua assinatura" e acompanhamento do pedido --

export function acertoContratoFicouPronto(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
  segundosDeEspera: number;
  quantidadeTentativas: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'contrato_ficou_pronto',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
      segundos_de_espera: params.segundosDeEspera,
      quantidade_tentativas: params.quantidadeTentativas,
    },
  });
}

export function acertoFalhouPreparoContrato(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  motivoFalha: string;
  propostaId: string;
  segundosDeEspera: number;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_preparo_contrato',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      motivo_falha: params.motivoFalha,
      proposta_id: params.propostaId,
      segundos_de_espera: params.segundosDeEspera,
    },
  });
}

export function acertoIniciouAssinatura(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
  popupBloqueado: boolean;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'iniciou_assinatura',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
      popup_bloqueado: params.popupBloqueado,
    },
  });
}

export function acertoAssinouNestaJanela(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'assinou_nesta_janela',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
    },
  });
}

export function acertoTentouContratoNovamente(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'tentou_contrato_novamente',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
    },
  });
}

export function acertoAbriuCancelamento(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_cancelamento',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
    },
  });
}

export function acertoCancelouPedido(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
  statusPropostaAnterior: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'cancelou_pedido',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
      status_proposta_anterior: params.statusPropostaAnterior,
    },
  });
}

export function acertoFalhouCancelamento(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  motivoFalha: string;
  status: number;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_cancelamento',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      motivo_falha: params.motivoFalha,
      status: params.status,
      proposta_id: params.propostaId,
    },
  });
}

export function acertoBaixouContrato(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'baixou_contrato',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
    },
  });
}

export function acertoBaixouComprovante(params: {
  consentimentoId: string | null;
  simulacaoId: string | null;
  propostaId: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'baixou_comprovante',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId, simulacao_id: params.simulacaoId }),
      proposta_id: params.propostaId,
    },
  });
}

// -- Aba Empréstimos --

export function acertoAcessouMeusEmprestimos(params: {
  quantidadePropostas: number;
  quantidadeAtivas: number;
  quantidadeEncerradas: number;
  estadoMaisRecente: string;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'acessou_meus_emprestimos',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
      quantidade_propostas: params.quantidadePropostas,
      quantidade_ativas: params.quantidadeAtivas,
      quantidade_encerradas: params.quantidadeEncerradas,
      estado_mais_recente: params.estadoMaisRecente,
    },
  });
}

export function acertoNaoTinhaEmprestimo() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'nao_tinha_emprestimo',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoFalhouListaEmprestimos(status: number) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'falhou_lista_emprestimos',
    tipo: 'onerror',
    payload: {
      ...queryStringService.getUtms(),
      status,
    },
  });
}

export function acertoTentouListaNovamente() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'tentou_lista_novamente',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

/**
 * Clique em "Acompanhar empréstimo" no card de quem não tem dívida ativa. Não faz parte do
 * catálogo de 59 eventos de BE-23872 — decisão comentada no card ao remover o genérico
 * `click_credito_clt`, que cobria este clique antes sem nome próprio.
 */
export function acertoAbriuAcompanhamento() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_acompanhamento',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

/**
 * Clique na aba "Empréstimos" da navegação da home. Mesmo caso de `acertoAbriuAcompanhamento`:
 * fora do catálogo de BE-23872, ganhou nome próprio ao remover o genérico `click_credito_clt`.
 */
export function acertoAbriuAbaEmprestimos() {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_aba_emprestimos',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
    },
  });
}

export function acertoAbriuPedidoDaLista(params: { propostaId: string; statusProposta: string; posicao: number }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'abriu_pedido_da_lista',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      proposta_id: params.propostaId,
      status_proposta: params.statusProposta,
      posicao: params.posicao,
    },
  });
}

// -- Pré-qualificação (BE-23876): duas perguntas antes da autorização --

export function acertoAcessouQualificacao(params: { pergunta: 'vinculo' | 'intencao'; origemAba: OrigemAbaCredito }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'acessou_qualificacao',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
      pergunta: params.pergunta,
      origem_aba: params.origemAba,
    },
  });
}

export function acertoDeclarouVinculo(resposta: VinculoDeclarado) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'declarou_vinculo',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      resposta,
    },
  });
}

export function acertoDeclarouIntencao(resposta: IntencaoDeclarada) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'declarou_intencao',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      resposta,
    },
  });
}

/** Só dispara quando as duas declarações liberam a passagem: quem é barrado emite `simulacao_resultado`. */
export function acertoPassouQualificacao(params: {
  vinculoDeclarado: Exclude<VinculoDeclarado, 'nao'>;
  intencaoDeclarada: Extract<IntencaoDeclarada, 'contratar'>;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'passou_qualificacao',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      vinculo_declarado: params.vinculoDeclarado,
      intencao_declarada: params.intencaoDeclarada,
    },
  });
}

/** Um evento para os três pontos do gate; sem custo no payload, porque o preço mora na tabela do contrato. */
export function acertoSimulacaoResultado(params: {
  status: OrigemMargem | 'bloqueado';
  motivo?: MotivoBloqueioCredito;
  consentimentoId?: string | null;
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'simulacao_resultado',
    tipo: 'onsuccess',
    payload: {
      ...queryStringService.getUtms(),
      ...semCamposVazios({ consentimento_id: params.consentimentoId }),
      status: params.status,
      ...semCamposVazios({ motivo: params.motivo }),
    },
  });
}

/** Separado de `simulacao_resultado` de propósito: mede se a pessoa chegou a ler a explicação. */
export function acertoViuBloqueio(motivoBloqueio: MotivoBloqueioCredito) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'viu_bloqueio',
    tipo: 'pageview',
    payload: {
      ...queryStringService.getUtms(),
      motivo_bloqueio: motivoBloqueio,
    },
  });
}

export function acertoSaiuDoBloqueio(params: {
  motivoBloqueio: MotivoBloqueioCredito;
  saida: 'principal' | 'secundaria';
}) {
  enviarEventoComIdentificadorSessao({
    categoria: 'credito',
    nome: 'saiu_do_bloqueio',
    tipo: 'click',
    payload: {
      ...queryStringService.getUtms(),
      motivo_bloqueio: params.motivoBloqueio,
      saida: params.saida,
    },
  });
}

// #endregion

// #region - Eventos Gift Card

const GRUPO_GIFT_CARD = {
  TESTE: 'teste',
  CONTROLE: 'controle',
} as const;

type GrupoGiftCard = (typeof GRUPO_GIFT_CARD)[keyof typeof GRUPO_GIFT_CARD];
type DeviceTypeGiftCard = 'mobile' | 'desktop';

function obterGrupoTesteControleGiftCard(): GrupoGiftCard {
  return sessionService.isFeatureFlagOn(FEATURE_FLAGS.CAMPANHA_GIFT_CARD)
    ? GRUPO_GIFT_CARD.TESTE
    : GRUPO_GIFT_CARD.CONTROLE;
}

function obterDeviceTypeGiftCard(): DeviceTypeGiftCard {
  if (typeof window.matchMedia !== 'function') {
    return 'desktop';
  }

  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
}

function payloadGiftCard(divida?: DividaPick<'id' | 'codigoCredor'>) {
  return {
    user_id: getHash(),
    grupo_teste_controle: obterGrupoTesteControleGiftCard(),
    device_type: obterDeviceTypeGiftCard(),
    timestamp: serverTimeService.syncedTime.toISOString(),
    ...(divida
      ? {
          divida_id: getIdBaseUnica({
            codigoCredor: divida.codigoCredor,
            idEntidade: divida.id,
          }),
        }
      : {}),
  };
}

export function acertoGiftCardModalEntradaView(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_entrada_view',
    tipo: 'pageview',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardModalEntradaCtaClick(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_entrada_cta_click',
    tipo: 'click',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardModalEntradaDismiss(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_entrada_dismiss',
    tipo: 'click',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardModalPosAcordoView(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_pos_acordo_view',
    tipo: 'pageview',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardModalPosAcordoCtaClick(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_pos_acordo_cta_click',
    tipo: 'click',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardModalPosAcordoDismiss(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_modal_pos_acordo_dismiss',
    tipo: 'click',
    payload: payloadGiftCard(divida),
  });
}

export function acertoGiftCardTagView(divida?: DividaPick<'id' | 'codigoCredor'>) {
  enviarEventoComIdentificadorSessao({
    categoria: 'negociacao',
    nome: 'gift_card_tag_view',
    tipo: 'pageview',
    payload: payloadGiftCard(divida),
  });
}

// #endregion

// #region - Eventos PWA

export type PlataformaPwa = 'ios' | 'android' | 'desktop';

/**
 * `appinstalled`: o navegador avisou a instalação (Android/Desktop). `primeira_abertura`: o app abriu
 * pela primeira vez instalado neste dispositivo, único sinal possível no iOS, que não avisa a
 * instalação, e que também conta quem instalou antes deste evento existir.
 */
export type DeteccaoInstalacaoPwa = 'appinstalled' | 'primeira_abertura';

export function acertoPwaInstalado(params: { plataforma: PlataformaPwa; deteccao: DeteccaoInstalacaoPwa }) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pwa',
    nome: 'pwa_instalado',
    tipo: 'onsuccess',
    payload: params,
  });
}

export function acertoPushPermissaoConcedida(plataforma: PlataformaPwa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pwa',
    nome: 'push_permissao_concedida',
    tipo: 'onsuccess',
    payload: { plataforma },
  });
}

export function acertoPushPermissaoNegada(plataforma: PlataformaPwa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pwa',
    nome: 'push_permissao_negada',
    tipo: 'onsuccess',
    payload: { plataforma },
  });
}

/**
 * Nome em inglês e `mensagemId` nulo para seguir o esquema dos eventos de notificação que o
 * `public/sw.js` publica no EventHub. A revogação não vem de um disparo.
 */
export function acertoPushPermissaoRevogada(plataforma: PlataformaPwa) {
  enviarEventoComIdentificadorSessao({
    categoria: 'pwa',
    nome: 'permission_revoked',
    tipo: 'onsuccess',
    payload: { plataforma, mensagemId: null },
  });
}

// #endregion
